import {
  type UploadedFile,
  api,
  formatDateWithDots,
  useDialog,
  useMutation,
  useQuery,
  useQueryClient,
  useRoleConf,
  useToaster,
} from "@pharmupp/p3-front-commons"
import { EyeOff } from "react-feather"
import type { MarketingOpTypesName } from "../../marketingOperationReferential"
import type { OperationValidationStatus } from "../../operationMarketing.model"
import type { ProofDocument, ReferentialLaboratory } from "../../types"
import { adhMarketingOPApiRoleConf } from "../conf"
import type { OperationStateEnum } from "../model"

export const useAdhMarketingOPDisplayApi = ({
  operationId = "",
  adherentId,
  enabled = true,
}: {
  operationId?: string
  adherentId?: string
  enabled?: boolean
}) => {
  const { readEndpoint, readQueryKey = [] } = useRoleConf(
    adhMarketingOPApiRoleConf(adherentId, operationId),
  )
  // OPERATION
  const { data: operation, isLoading: loading } = useQuery<
    ApiMarketingOperationDisplay,
    unknown,
    MarketingOperationDisplay
  >({
    queryKey: readQueryKey,
    queryFn: () => {
      return api.get(readEndpoint ?? "")
    },
    select: selectOperation,
    enabled: enabled && !!readEndpoint,
  })

  return { operation, loading }
}

const selectOperation = (
  ope: ApiMarketingOperationDisplay,
): MarketingOperationDisplay => ({
  ...ope,
  validityStartDate: formatDateWithDots(ope.validityStartDate),
  validityEndDate: formatDateWithDots(ope.validityEndDate),
  deliveryDate: new Date(ope.deliveryDate).toLocaleDateString("fr", {
    day: "numeric",
    month: "long",
  }),
})

export type ApiMarketingOperationDisplay = {
  id: number
  programmedPublication: boolean
  publicationDate?: string | null
  name: string
  validityStartDate: string
  validityEndDate: string
  image?: UploadedFile | null
  description?: string | null
  registered: boolean
  state: OperationStateEnum
  validationStatus?: OperationValidationStatus
  rejectionReason?: string
  kitOrderStartDate: string
  kitOrderEndDate: string
  kitOrderStartDateFormatted: string
  kitOrderEndDateFormatted: string
  deliveryCarrier?: string | null
  deliveryDate: string
  nbLoyaltyPoints?: number | null
  participationBonus?: number | null
  completionProofDetails?: string | null
  nbAmmProducts: number
  nbProofDocuments: number
  nbProofDocumentsRecommended: number
  nbProofDocumentsMin: number
  nbProofDocumentsMax: number
  qrCodeToken?: string | null
  proofDocuments?: ProofDocument[]
  type: MarketingOpTypesName
  partnerLaboratories: ReferentialLaboratory[]
  hidden?: boolean
  // {
  //     document: UploadedFile
  //     validationStatus: OperationValidationStatus
  //     rejectionReason?: string
  //   }[]
} & AmmProducts

type AmmProducts =
  | {
      hasAmmProducts: true
      nbAmmProducts: number
    }
  | {
      hasAmmProducts: false
    }

export type MarketingOperationDisplay = Omit<
  ApiMarketingOperationDisplay,
  "registered" | "validationStatus"
>

export const useSubscriptionApi = ({
  operationId = "",
  adherentId,
}: {
  operationId?: string
  adherentId?: string
}) => {
  const toaster = useToaster()
  const dialog = useDialog()
  const queryClient = useQueryClient()
  const {
    queryKey: listQueryKey,
    readQueryKey = [],
    subscribeEndpoint,
    unsubscribeEndpoint,
  } = useRoleConf(adhMarketingOPApiRoleConf(adherentId, operationId))

  // SUBSCRIPTION
  const { mutateAsync: subscribeMutation, isPending: isSubscribing } =
    useMutation<ApiMarketingOperationDisplay>({
      mutationFn: () =>
        api.put(subscribeEndpoint ?? "", {
          headers: { "Content-Type": "application/json" },
        }),
      onSuccess: (data) => {
        toaster.success("Inscription confirmée")
        queryClient.setQueryData(readQueryKey, data)
        queryClient.invalidateQueries({ queryKey: listQueryKey })
        // Operations list of the adherent home search
        queryClient.invalidateQueries({ queryKey: ["search", "operations"] })
      },
      onError: () => {
        toaster.error("Erreur lors de la souscription")
      },
    })

  // UNSUBSCRIPTION
  const { mutateAsync: unsubscribe, isPending: isUnsubscribing } = useMutation({
    mutationFn: () =>
      api.put(unsubscribeEndpoint ?? "", {
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: readQueryKey })
    },
    onError: () => {
      toaster.error("Erreur lors de la désinscription")
    },
  })

  const confirmUnsubscribe = () => {
    const operation =
      queryClient.getQueryData<ApiMarketingOperationDisplay>(readQueryKey)
    dialog.confirm({
      title: "Attention",
      message: (
        <div style={{ textAlign: "center" }}>
          Souhaitez-vous vraiment vous désinscrire de l&apos;opération marketing :
          <br />
          <b>{operation?.name}</b> ?
        </div>
      ),
      validateAction() {
        unsubscribe()
      },
    })
  }

  return {
    subscribe: subscribeEndpoint ? subscribeMutation : undefined,
    isSubscribing,
    unsubscribe: unsubscribeEndpoint ? confirmUnsubscribe : undefined,
    isUnsubscribing,
  }
}

export const useHideParticipationApi = ({
  operationId = "",
  adherentId,
}: {
  operationId?: string
  adherentId?: string
}) => {
  const toaster = useToaster()
  const dialog = useDialog()
  const queryClient = useQueryClient()
  const { hideEndpoint, readQueryKey = [] } = useRoleConf(
    adhMarketingOPApiRoleConf(adherentId, operationId),
  )

  const { mutateAsync: mutateParticipation } = useMutation({
    mutationFn: () =>
      api.put(hideEndpoint ?? "", {
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: readQueryKey })
    },
    onError: () => {
      toaster.error("Erreur lors du masquage de l'opération")
    },
  })

  const hideParticipation = () => {
    const operation =
      queryClient.getQueryData<ApiMarketingOperationDisplay>(readQueryKey)
    dialog.confirm({
      title: "Opération masquée",
      Icon: EyeOff,
      message: (
        <div style={{ textAlign: "center" }}>
          Vous-êtes sur le point de masquer l&apos;opération <b>{operation?.name}</b>
          , elle ne vous sera plus proposée en suivi.
        </div>
      ),
      validateAction() {
        mutateParticipation()
      },
    })
  }

  return { hideParticipation: hideEndpoint ? hideParticipation : undefined }
}
